// AddUpdateOnDutyApprovalView.jsx

import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { API_ENDPOINTS } from "../../../config/api/apiCall";
import OnDutyApprovalActionMenu from "./OnDutyApprovalActionMenu";

/* ---------- Styles ---------- */
const LABEL_STYLE = {
  fontSize: "0.85rem",
  color: "#ffffff",
};

const INPUT_PROPS_READONLY = {
  size: "small",
  fullWidth: true,
  InputProps: { readOnly: true },
  sx: {
    "& .MuiInputBase-input": {
      color: "#ffffff",
      fontSize: "0.85rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#777",
    },
  },
};

const BUTTON_STYLE = {
  fontSize: "0.8rem",
  color: "#ffffff",
  textTransform: "none",
  borderColor: "#aaa",
};

const AddUpdateOnDutyApprovalView = ({
  formData,
  setIsFormOpen,
  reloadList
}) => {
  const [data, setData] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!formData) return;
    const record = Array.isArray(formData) ? formData[0] : formData;
    setData(record);
    setLoading(false);
  }, [formData]);

  const isEdit = !!data?.id_on_duty;
const isApproved =
  data?.status === "APPROVED" || data?.approval_status === 1;
  /* ---------- Attachment Viewer with preview ---------- */
  const openAttachment = (base64, expenseType = "expense", amount = "0") => {
    if (!base64) return;

    try {
      const cleanBase64 = base64.replace(/\s/g, "");
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
      let mimeType = "text/plain"; // default

      // Simple detection: PDF files start with '%PDF'
      if (byteCharacters.startsWith("%PDF")) mimeType = "application/pdf";

      const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
      const url = URL.createObjectURL(blob);

      // Open in new tab
      const newWindow = window.open(url, "_blank");
      if (!newWindow) {
        alert("Please allow popups to view the attachment.");
        return;
      }

      // Optionally, set filename for download
      const filename = `${expenseType}_${amount}${mimeType === "application/pdf" ? ".pdf" : ".txt"}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      // Add a small delay to allow tab to open before triggering download if needed
      setTimeout(() => a.click(), 500);
    } catch (err) {
      console.error("Failed to open attachment:", err);
    }
  };

  const handleApprove = async () => {
    try {
      const payload = {
        p_id_on_duty: data?.id_on_duty,
        p_id_employee: 3,
        p_approved_by: 3,
        p_company_id: Number(localStorage.getItem("companyid")),
        p_plant_id: 1,
        p_remarks: remarks,
      };

      await fetch(API_ENDPOINTS.onduty.Approvalupdate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      reloadList();
      setIsFormOpen(false);
    } catch (err) {
      console.error("Approval failed:", err);
    }
  };

  const handleExit = () => setIsFormOpen(false);

  if (loading || !data) return null;

  return (
    <Box>
      {/* <OnDutyApprovalActionMenu
        isEdit={isEdit}
        onSave={handleApprove}
        onDelete={() => {}}
        onClear={() => setData(null)}
        onExit={handleExit}
      /> */}
<OnDutyApprovalActionMenu
  isEdit={isEdit}
  saveDisabled={isApproved}
  onSave={handleApprove}
  onDelete={() => {}}
  onClear={() => setData(null)}
  onExit={handleExit}
/>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              {...INPUT_PROPS_READONLY}
              label="Travel Purpose"
              InputLabelProps={{ shrink: true, sx: LABEL_STYLE }}
              value={data.travel_purpose || ""}
            />
          </Grid> 

          <Grid item xs={6}>
            <TextField
              {...INPUT_PROPS_READONLY}
              label="Location"
              InputLabelProps={{ shrink: true, sx: LABEL_STYLE }}
              value={data.location || ""}
            />
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={6}>
              <DatePicker
                label="From Date"
                value={data.on_duty_from_date ? dayjs(data.on_duty_from_date) : null}
                readOnly
                slotProps={{
                  textField: {
                    ...INPUT_PROPS_READONLY,
                    InputLabelProps: { shrink: true, sx: LABEL_STYLE },
                  }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="To Date"
                value={data.on_duty_to_date ? dayjs(data.on_duty_to_date) : null}
                readOnly
                slotProps={{
                  textField: {
                    ...INPUT_PROPS_READONLY,
                    InputLabelProps: { shrink: true, sx: LABEL_STYLE },
                  }
                }}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>

        <Typography mt={3} mb={1} sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#ffffff" }}>
          Expenses
        </Typography>

        {(data.expense_list || []).map((exp, idx) => (
          <Grid container spacing={1} key={idx} mb={1}>
            <Grid item xs={4}>
              <TextField
                {...INPUT_PROPS_READONLY}
                label="Expense Type"
                InputLabelProps={{ sx: LABEL_STYLE }}
                value={exp.expence_type || ""}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                {...INPUT_PROPS_READONLY}

                            label="Amount"
                InputLabelProps={{ sx: LABEL_STYLE }}
                value={exp.amount || ""}
              />
            </Grid>

            <Grid item xs={5}>
              <Button
                variant="outlined"
                size="small"
                sx={BUTTON_STYLE}
                onClick={() =>
                  openAttachment(exp.bill_attachement, exp.expence_type || "expense", exp.amount || "0")
                }
              >
                {exp.bill_attachement ? "View Attachment" : "No File"}
              </Button>
            </Grid>
          </Grid>
        ))}

        <TextField
          label="Remarks"
          multiline
          rows={3}
          fullWidth
          size="small"
          sx={{
            mt: 2,
            "& .MuiInputBase-input": { color: "#ffffff", fontSize: "0.85rem" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#777" }
          }}
          InputLabelProps={{ sx: LABEL_STYLE }}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        <Button
  variant="contained"
  sx={{
    mt: 2,
    fontSize: "0.85rem",
    textTransform: "none",
    backgroundColor: isApproved ? "#777" : "#4caf50",
  }}
  disabled={isApproved}
  onClick={handleApprove}
>
  {isApproved ? "Approved" : "Approve"}
</Button>

        {/* <Button
          variant="contained"
          sx={{
            mt: 2,
            fontSize: "0.85rem",
            textTransform: "none",
            backgroundColor: "#4caf50"
          }}
          onClick={handleApprove}
        >
          Approve
        </Button> */}
      </Box>
    </Box>
  );
};

export default AddUpdateOnDutyApprovalView;
